import React, { useEffect, useRef, useState } from 'react';

/**
 * Bottom-right slide-in banner showing what the same deal would have cost in
 * marketplace fees (see src/utils/marketplaceSavings.js for the math and the
 * "next major marketplace" wording rationale). Shown only on the checkout
 * pages (TradeConfirm for both traders, SellerPurchaseConfirm for the seller)
 * — the moment real money is on the table — once per storageKey (offer +
 * user), then never again, even across visits. Deliberately NOT on the
 * progress pages: repeating the same number there dulls it (Jason, 2026-09-25).
 *
 * Dev aid: on localhost, append ?previewSavings=42 to any page that mounts
 * this component to force it open with that amount, ignoring the once-only
 * marker. Production builds ignore the param.
 */
const SHOWN_PREFIX = 'pxcSavingsBannerShown:';
const AUTO_DISMISS_MS = 15000;

function alreadyShown(key) {
  try {
    return window.localStorage.getItem(SHOWN_PREFIX + key) === '1';
  } catch {
    return false;
  }
}

function markShown(key) {
  try {
    window.localStorage.setItem(SHOWN_PREFIX + key, '1');
  } catch {
    // Private windows etc.: banner may reappear next visit, which is fine.
  }
}

function devPreviewAmount() {
  if (process.env.NODE_ENV !== 'development') return 0;
  try {
    const v = new URLSearchParams(window.location.search).get('previewSavings');
    return v ? Math.round(Number(v)) || 0 : 0;
  } catch {
    return 0;
  }
}

export default function SavingsBanner({
  storageKey,
  amountDollars,
  ownLabel = 'your pedal',
  receivingLabel = 'this one',
  variant = 'trade',
}) {
  const preview = devPreviewAmount();
  const amount = preview || amountDollars;

  // The show/don't-show decision is LATCHED at first render. It must not be
  // recomputed per render: markShown() below flips alreadyShown() to true, so
  // a live check would unmount the banner on the page's next re-render,
  // before the slide-in timer ever fired (bug found on 2026-09-25 — the
  // banner marked itself shown and died unseen on every checkout page).
  const [shouldShow] = useState(
    () => Boolean(storageKey) && amount > 0 && (preview > 0 || !alreadyShown(storageKey))
  );

  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timersRef = useRef([]);

  useEffect(() => {
    if (!shouldShow) return undefined;
    // Slide in after the page settles, slide out on the auto-dismiss timer.
    // The once-only marker is written when the banner actually APPEARS, not at
    // mount: a user who navigates away inside the 900ms delay hasn't seen it
    // and must not lose their one showing.
    timersRef.current.push(setTimeout(() => {
      if (!preview) markShown(storageKey);
      setVisible(true);
    }, 900));
    timersRef.current.push(setTimeout(() => setVisible(false), AUTO_DISMISS_MS));
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, storageKey]);

  if (!shouldShow || dismissed) return null;

  // Copy is deliberately a factual counterfactual ("would have cost"), never a
  // savings claim ("you avoided/saved") — we charge our own $3, so an avoided-
  // fees claim would overstate (Jason, 2026-09-25).
  const amountEl = (
    <strong style={{ color: '#C9A227', fontSize: '19px', fontWeight: 800 }}>${amount}</strong>
  );
  const body = variant === 'sale' ? (
    <>
      <strong>Nice job!</strong> Selling this pedal on the next major marketplace would have
      cost about {amountEl} <strong>in fees alone, not including shipping.</strong>
    </>
  ) : (
    <>
      <strong>Nice job!</strong> Selling {ownLabel} to buy {receivingLabel} would have cost
      about {amountEl} <strong>in fees alone, not including shipping both ways,</strong> on
      the next major marketplace.
    </>
  );

  return (
    <div
      role="status"
      className="savings-banner"
      style={{
        position: 'fixed',
        right: '16px',
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        maxWidth: 'min(360px, calc(100vw - 32px))',
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(14px) saturate(1.5)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.5)',
        border: '1px solid rgba(255, 255, 255, 0.65)',
        borderRadius: '16px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22), 0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '16px 38px 16px 18px',
        zIndex: 1200,
        transform: visible ? 'translateX(0)' : 'translateX(calc(100% + 24px))',
        transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
        color: '#4A5759',
        fontSize: '15px',
        lineHeight: 1.5,
        textAlign: 'left',
      }}
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{
          position: 'absolute',
          top: '6px',
          right: '8px',
          background: 'none',
          border: 'none',
          color: '#9ca3af',
          fontSize: '18px',
          lineHeight: 1,
          cursor: 'pointer',
          padding: '4px',
        }}
      >
        ×
      </button>
      {body}
    </div>
  );
}
